import { useState, type ReactElement, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useProfile } from '@/lib/auth';
import type { Profile } from '@/lib/api';
import { ProfileEditForm } from './ProfileEditForm';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * Vista de perfil: muestra todos los campos del usuario y permite editar.
 *
 * El "modo edición" sustituye la vista por el formulario sin necesidad de
 * modal — más simple a nivel de a11y y manejo de foco.
 */
export function ProfileView(): ReactElement {
  const { data, isLoading, isError, error } = useProfile();
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <p role="status" aria-live="polite" className="text-slate-700">
        Cargando perfil…
      </p>
    );
  }

  if (isError || !data) {
    return (
      <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
        No se pudo cargar el perfil. {error?.message ?? ''}
      </p>
    );
  }

  if (editing) {
    return (
      <ProfileEditForm
        profile={data}
        onCancel={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    );
  }

  return (
    <section aria-labelledby="profile-title" className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 id="profile-title" className="text-2xl font-semibold tracking-tight text-slate-900">
            Mi perfil
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Última actualización: {formatDate(data.updatedAt)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          Editar
        </button>
      </header>

      <ProfileSection title="Identidad">
        <Row label="Nombre" value={data.name} />
        <Row label="Edad" value={fmt(data.age)} />
        <Row label="Sexo" value={fmtSex(data.sex)} />
        <Row label="Peso" value={data.weightKg !== null ? `${data.weightKg} kg` : '—'} />
        <Row label="Talla" value={data.heightCm !== null ? `${data.heightCm} cm` : '—'} />
      </ProfileSection>

      <ProfileSection title="Métricas de entrenamiento">
        <Row label="FTP" value={data.ftpW !== null ? `${data.ftpW} W` : '—'} metric="ftp" />
        <Row label="LTHR" value={data.lthrBpm !== null ? `${data.lthrBpm} bpm` : '—'} metric="lthr" />
        <Row label="T-pace running" value={data.tPaceRun ?? '—'} metric="tPaceRun" />
        <Row label="T-pace swimming" value={data.tPaceSwim ?? '—'} metric="tPaceSwim" />
      </ProfileSection>

      <ProfileSection title="Preferencias">
        <Row label="Timezone" value={data.timezone} />
        <Row label="Unidades" value={data.units === 'metric' ? 'Métrico' : 'Imperial'} />
        <Row label="Objetivo del año" value={data.yearGoal ?? '—'} />
      </ProfileSection>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Semana
        </h2>
        <p className="mt-2 text-sm text-slate-700">
          Configura qué disciplina prefieres cada día y la auto-generación
          dominical del plan.
        </p>
        <Link
          to="/perfil/semana"
          className="mt-3 inline-block text-sm font-medium text-teal-700 hover:text-teal-800"
        >
          Configurar semana →
        </Link>
      </section>
    </section>
  );
}

function fmt(v: number | null): string {
  return v === null ? '—' : String(v);
}

function fmtSex(sex: Profile['sex']): string {
  if (sex === 'M') return 'Masculino';
  if (sex === 'F') return 'Femenino';
  if (sex === 'X') return 'Otro / sin especificar';
  return '—';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-ES');
  } catch {
    return iso;
  }
}

function ProfileSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <dl className="mt-3 space-y-2">{children}</dl>
    </section>
  );
}

function Row({
  label,
  value,
  metric,
}: {
  label: string;
  value: string;
  /** Clave del glosario M2.32.E — añade icono "?" junto al label. */
  metric?: MetricKey;
}): ReactElement {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-2 text-sm">
      <dt className="text-slate-600">
        {metric ? <MetricTooltip metric={metric}>{label}</MetricTooltip> : label}
      </dt>
      <dd className="text-slate-900">{value}</dd>
    </div>
  );
}
